import { useMemo } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CountdownTimer from "@/components/countdown-timer";
import { CompactPrizeBadges } from "@/components/challenges/CompactPrizeBadges";
import { 
  Trophy, 
  Calendar, 
  Gift, 
  Clock, 
  ArrowLeft,
  LogIn,
  UserPlus,
  Lock,
  AlertCircle
} from "lucide-react";
import { motion } from "framer-motion";

// TypeScript interfaces
interface Competition {
  id: number;
  title: string;
  description?: string;
  startDate: string;
  startTime?: string;
  endDate: string;
  endTime?: string;
  prize?: string;
  prize1?: string;
  prize2?: string;
  prize3?: string;
  status?: string;
}

interface MobileCompetitionGuestViewProps {
  competition: Competition;
  competitionId: number;
  referralCode?: string;
}

// Helper function to transform competition data to PrizeDetails format
function transformCompetitionToPrizeDetails(competition: Competition) {
  const prizeDetails: any = {};

  if (competition?.prize1) {
    prizeDetails["1st"] = { item: competition.prize1, value: 0 };
  }
  if (competition?.prize2) {
    prizeDetails["2nd"] = { item: competition.prize2, value: 0 };
  }
  if (competition?.prize3) {
    prizeDetails["3rd"] = { item: competition.prize3, value: 0 };
  }

  // Fallback for old single prize field
  if (
    !competition?.prize1 &&
    !competition?.prize2 &&
    !competition?.prize3 &&
    competition?.prize
  ) {
    prizeDetails["1st"] = { item: competition.prize, value: 0 };
  }

  return prizeDetails;
}

export default function MobileCompetitionGuestView({ 
  competition, 
  competitionId, 
  referralCode 
}: MobileCompetitionGuestViewProps) {
  const [, navigate] = useLocation();

  // Calculate competition timing status
  const competitionStatus = useMemo(() => {
    const now = new Date();
    const startDateOnly = competition.startDate 
      ? new Date(competition.startDate).toISOString().split("T")[0]
      : null;
    const startTimeStr = competition.startTime || "00:00";
    const startDateTime = startDateOnly
      ? new Date(`${startDateOnly}T${startTimeStr}:00+02:00`)
      : null;

    const endDateOnly = competition.endDate
      ? new Date(competition.endDate).toISOString().split("T")[0]
      : null;
    const endTimeStr = competition.endTime || "23:59";
    const endDateTime = endDateOnly
      ? new Date(`${endDateOnly}T${endTimeStr}:59+02:00`)
      : null;

    const isExpired = endDateTime ? endDateTime < now : false;
    const isScheduled = startDateTime ? startDateTime > now : false;

    return {
      isExpired,
      isScheduled,
      isActive: !isExpired && !isScheduled,
      startDateTime,
      endDateTime
    };
  }, [competition.startDate, competition.startTime, competition.endDate, competition.endTime]);

  const prizeDetails = transformCompetitionToPrizeDetails(competition);

  // Handle navigation with proper redirect and referral parameters
  const handleNavigation = (action: 'register' | 'login') => {
    const currentUrl = window.location.pathname + window.location.search;
    const redirectParam = encodeURIComponent(currentUrl);

    let authUrl = `/m/auth?redirect=${redirectParam}`;

    // Add referral code if present
    if (referralCode) {
      authUrl += `&ref=${encodeURIComponent(referralCode)}`;
    }

    // Add action type for better UX
    authUrl += `&action=${action}`;

    navigate(authUrl);
  };

  const handleBackNavigation = () => {
    navigate("/m/competitions");
  };

  // Error handling for missing competition data
  if (!competition) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
        <div className="px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Competition Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The competition you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={handleBackNavigation} data-testid="button-back-to-competitions">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Competitions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen w-full max-w-full bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden"
      style={{ 
        paddingBottom: 'max(6rem, calc(6rem + env(safe-area-inset-bottom)))',
        minWidth: '100vw',
        width: '100vw'
      }}
      data-testid="mobile-competition-guest-view"
    >
      {/* Mobile Header */}
      <div 
        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
        style={{ paddingTop: 'max(3rem, calc(3rem + env(safe-area-inset-top)))' }}
      >
        <div className="px-4 pb-6">
          <div className="flex items-center mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackNavigation}
              className="text-white hover:bg-white/20 mr-3 p-2"
              data-testid="button-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold line-clamp-2" data-testid="text-competition-title">
                {competition.title}
              </h1>
              <div className="flex items-center space-x-4 mt-1">
                <Badge 
                  className={`text-xs ${
                    competitionStatus.isExpired 
                      ? 'bg-red-600' 
                      : competitionStatus.isScheduled 
                        ? 'bg-yellow-600' 
                        : 'bg-green-600'
                  }`}
                  data-testid="badge-competition-status"
                >
                  {competitionStatus.isExpired ? 'Ended' : competitionStatus.isScheduled ? 'Scheduled' : 'Active'}
                </Badge>
                {!competitionStatus.isExpired && !competitionStatus.isScheduled && competition.endDate && (
                  <div className="text-xs text-blue-100" data-testid="text-countdown">
                    <CountdownTimer 
                      endDate={competition.endDate}
                      endTime={competition.endTime}
                      compact={true}
                      isActive={competitionStatus.isActive}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call-to-Action Section - Moved to top for better UX */}
      <div className="px-4 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="text-center space-y-6">
                {/* Login Required Icon and Message */}
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                      <Lock className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-testid="text-login-required">
                      Registration Required
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400" data-testid="text-login-message">
                      Please register or login to enter this competition and start earning entries
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    className="w-full text-white shadow-lg font-semibold py-4 hover:opacity-90"
                    size="lg"
                    onClick={() => handleNavigation('register')}
                    data-testid="button-register"
                  >
                    <UserPlus className="mr-2 h-5 w-5" />
                    Register Now
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 py-4 font-medium"
                    onClick={() => handleNavigation('login')}
                    data-testid="button-login-to-enter"
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Login to Enter
                  </Button>
                </div>

                {/* Additional Info */}
                {referralCode && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-center space-x-2">
                      <Gift className="h-4 w-4" />
                      <span data-testid="text-referral-info">
                        You were referred by a friend - join now to get bonus entries!
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Prize Section */}
        {Object.keys(prizeDetails).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="rounded-2xl shadow-lg border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Gift className="h-5 w-5 mr-2 text-yellow-600" />
                  Prizes to Win
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CompactPrizeBadges prizeDetails={prizeDetails} />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Competition Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="rounded-2xl shadow-lg border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                Competition Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status and Timing */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status:</span>
                <Badge 
                  variant={competitionStatus.isActive ? "default" : "secondary"}
                  className={
                    competitionStatus.isExpired 
                      ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" 
                      : competitionStatus.isScheduled 
                        ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                        : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  }
                  data-testid="badge-status-detail"
                >
                  {competitionStatus.isExpired ? 'Competition Ended' : competitionStatus.isScheduled ? 'Starting Soon' : 'Active Now'}
                </Badge>
              </div>

              {/* Countdown Timer */}
              {!competitionStatus.isExpired && competition.endDate && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      {competitionStatus.isScheduled ? 'Starts in:' : 'Ends in:'}
                    </div>
                    <CountdownTimer 
                      endDate={competition.endDate}
                      endTime={competition.endTime}
                      startDate={competitionStatus.isScheduled ? competition.startDate : undefined}
                      startTime={competitionStatus.isScheduled ? competition.startTime : undefined}
                      isActive={!competitionStatus.isExpired}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Description */}
        {competition.description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card className="rounded-2xl shadow-lg border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">About This Competition</CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 dark:text-gray-300 leading-relaxed"
                  data-testid="text-competition-description"
                >
                  {competition.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

      </div>
    </div>
  );
}